<!--
  text-base pins the badges: they are sized in em, and a card in a text-sm column would otherwise
  draw its kinds under the 12px floor.
-->
<div class="relative aspect-video overflow-hidden text-base">
  <!-- Unlayered, wash included, so the stretched link takes a press on it and the raised badges, flags
    and controls stay above the preparing wash. -->
  <mib-thumbnail
    size="fill"
    labelMode="caption"
    [src]="case().thumbnail"
    [priority]="priority()"
    [unavailable]="case().unavailable"
    [busy]="case().preparing"
    [busyIcon]="busyIcon()" />

  @if (badges().length > 0 || case().live) {
    <!-- Raised like the controls: what kind a case is stays true while it is still being made. -->
    <div class="absolute top-2 left-2 z-10 flex flex-col items-start gap-1">
      @for (badge of badges(); track badge.label) {
        <span mibBadge [palette]="badge.palette">{{ badge.label }}</span>
      }
      @if (case().live) {
        <!-- 0.04em in the design: wider is the nearest step, and 600 the heaviest the scale has. -->
        <span mibBadge theme="danger" class="gap-1.5 font-semibold tracking-wider uppercase">
          <span
            class="size-1.5 rounded-full bg-white animate-pulse motion-reduce:animate-none"
            aria-hidden="true"></span>
          {{ liveLabel() }}
        </span>
      }
    </div>
  }

  <!-- Above the stretched link, so the two of them take the press and the link does not. -->
  <div class="absolute top-2 right-2 z-10 flex gap-1">
    @if (case().favourite !== undefined) {
      <button
        type="button"
        [class]="discClasses"
        [attr.aria-pressed]="case().favourite"
        [attr.aria-label]="case().favourite ? removeFavouriteLabel() : addFavouriteLabel()"
        (click)="toggleFavourite()">
        <svg mib-icon name="star" filled [class]="starClasses()"></svg>
      </button>
    }
    @if (case().share; as share) {
      <button
        type="button"
        [class]="shareClasses"
        [attr.aria-label]="shareLabel()"
        [attr.aria-disabled]="share.disabled ? 'true' : null"
        [attr.aria-describedby]="share.disabled ? shareReasonId : null"
        [mibTooltip]="share.disabled ?? ''"
        mibTooltipKeepOpen
        (click)="shareCase()">
        <svg mib-icon name="share-stroke" class="size-4"></svg>
      </button>
      @if (share.disabled; as reason) {
        <span class="sr-only" [id]="shareReasonId">{{ reason }}</span>
      }
    }
    @if (menu(); as menu) {
      <button type="button" [mibMenuTrigger]="menu" [class]="discClasses" [attr.aria-label]="actionsLabel()">
        <svg mib-icon name="ellipsis" class="size-4"></svg>
      </button>
    }
  </div>

  @if (flags().length > 0) {
    <!--
      Raised, because the stretched link's ::after covers the picture and a pointer that never
      enters a flag never opens its tooltip. The cost is that these discs are not part of the
      link's hit area — which is already true of the star and the menu beside them.
    -->
    <div class="absolute bottom-2 left-2 z-10 flex items-center gap-2">
      <!-- The icon alone: it says what it means on hover, and to a screen reader through its label. -->
      @for (flag of flags(); track $index) {
        <span role="img" [attr.aria-label]="flag.tooltip" [mibTooltip]="flag.tooltip" [class]="flagClasses(flag)">
          <svg mib-icon [name]="flag.icon" class="size-4" aria-hidden="true"></svg>
          @if (flag.badge; as badge) {
            <span [class]="flagBadgeClasses(badge)">
              <svg mib-icon [name]="badge.icon" class="size-3" aria-hidden="true"></svg>
            </span>
          }
        </span>
      }
    </div>
  }

  @if (duration()) {
    <span [class]="durationClasses">{{ duration() }}</span>
  }
</div>

<!--
  Under the title, whatever the page writes is a line, and the default slot takes it: nothing to
  select means nothing a page can misspell and lose in silence, and an @if around a line projects
  as any content does, where a named slot would have dropped it. The skin goes on each **child**
  and not on a tag of ours, so a page reaches for whatever element it means — and two products
  still write the same line.
-->
<div [class]="detailsClasses">
  @if (case().href; as href) {
    <a
      [href]="href"
      [class]="linkClasses"
      (click)="open($event)"
      (focus)="showTitleOnFocus($event)"
      (blur)="titleTooltip()?.hide()"
      ><ng-container [ngTemplateOutlet]="title"
    /></a>
  } @else {
    <p [class]="headingClasses"><ng-container [ngTemplateOutlet]="title" /></p>
  }

  <div [class]="linesClasses">
    <ng-content />
  </div>
</div>

<ng-template #title
  ><span
    #check="checkTruncate"
    #titleTooltip="mibTooltip"
    [class]="titleClasses"
    [mibCheckTruncate]="case().title"
    [mibTooltip]="check.tooltipText()"
    >{{ case().title }}</span
  ></ng-template
>

<!--
  Renders nothing here: the panel is an overlay and the card keeps only a reference to it, which
  it hands the disc drawn over the picture. Taken last so it disturbs neither block, while a page
  writes it first — where the reader sees it.
-->
<ng-content select="mib-menu" />
